import { useContext } from "react";
import { FormikContext, getIn } from "formik";
import PropTypes from "prop-types";

const hasOwn = (value) => value !== undefined;

/**
 * @typedef {object} UseUIFieldInput
 * @property {string} [name]
 * @property {*} [value]
 * @property {boolean} [checked]
 * @property {boolean} [error]
 * @property {import("react").ReactNode} [helperText]
 */

/**
 * @param {UseUIFieldInput} [input]
 */
const useUIField = ({
  name,
  value,
  checked,
  error,
  helperText,
} = {}) => {
  const formik = useContext(FormikContext);
  const inFormik = Boolean(formik && name);

  const formikValue = formik && name ? getIn(formik.values, name) : undefined;
  const formikTouched = formik && name ? getIn(formik.touched, name) : undefined;
  const formikError = formik && name ? getIn(formik.errors, name) : undefined;
  const showMetaError = Boolean(formikError) && Boolean(formikTouched || formik.submitCount > 0);

  return {
    formik,
    inFormik,
    name,
    value: hasOwn(value) ? value : formikValue,
    checked: hasOwn(checked) ? checked : Boolean(formikValue),
    metaError: formikError,
    showError: hasOwn(error) ? error : showMetaError,
    // When the caller explicitly controls `error`, `helperText` must follow the same
    // override — otherwise a field can show error text (from Formik's raw touched/error
    // state) without the matching red border/text when the caller's `error` is false.
    helperText: hasOwn(error)
      ? error
        ? formikError || helperText
        : helperText
      : showMetaError
      ? formikError
      : helperText,
    setValue: (nextValue) => {
      if (inFormik && name) {
        formik.setFieldValue(name, nextValue);
      }
    },
    setTouched: (isTouched = true, shouldValidate = true) => {
      if (inFormik && name) {
        formik.setFieldTouched(name, isTouched, shouldValidate);
      }
    },
  };
};

export default useUIField;

useUIField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  checked: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.node,
};

useUIField.defaultProps = {
  value: undefined,
  checked: undefined,
  error: undefined,
  helperText: undefined,
};